import type { Variants } from 'framer-motion';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PropsWithChildren } from 'react';

interface DownloadIconBadgeProps extends PropsWithChildren {
  /** Number of exports that are currently queued or running */
  processingCount: number;
  /** Number of completed exports the user hasn't viewed yet */
  unviewedCount: number;
}

/**
 * DownloadIconBadge - Wraps a download icon with status indicators.
 *
 * Shows:
 * - A spinning ring border when exports are processing (Queued/Running)
 * - A purple count badge (top-right) for completed-but-unviewed exports
 * Both can be shown simultaneously.
 */
export function DownloadIconBadge({
  children,
  processingCount,
  unviewedCount,
}: DownloadIconBadgeProps) {
  const shouldReduceMotion = useReducedMotion();

  const isProcessing = processingCount > 0;
  const hasUnviewed = unviewedCount > 0;

  const badgeVariants: Variants = shouldReduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { scale: 0, opacity: 0 },
        animate: {
          scale: 1,
          opacity: 1,
          transition: { type: 'spring', stiffness: 500, damping: 25 },
        },
        exit: { scale: 0, opacity: 0, transition: { duration: 0.15 } },
      };

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Spinning processing ring */}
      {isProcessing && (
        <span
          role="status"
          aria-label={`${processingCount} export${processingCount === 1 ? '' : 's'} in progress`}
          className={`border-t-chariot-brand-primary pointer-events-none absolute inset-0 rounded-full border-2 border-transparent ${
            shouldReduceMotion ? '' : 'animate-spin'
          }`}
          style={{ borderRadius: '50%' }}
        />
      )}

      {children}

      {/* Unviewed count badge */}
      <AnimatePresence>
        {hasUnviewed && (
          <motion.span
            key="unviewed-badge"
            variants={badgeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="bg-chariot-brand-primary absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[10px] font-semibold text-white"
            aria-label={`${unviewedCount} new export${unviewedCount === 1 ? '' : 's'} ready`}
          >
            {unviewedCount > 9 ? '9+' : unviewedCount}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
